/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  Optional,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import {DateAdapter, MD_DATE_FORMATS, MdDateFormats} from '@angular/material/core';
import {MdCalendarCell} from './calendar-body';
import {coerceDateProperty} from './coerce-date-property';
import {createMissingDateImplError} from './datepicker-errors';
/**
 * An internal component used to display a single year in the datepicker.
 * \@docs-private
 */
export class MdYearView<D> implements AfterContentInit {
/**
 * The date to display in this year view (everything other than the year is ignored).
 * @return {?}
 */
get activeDate(): D { return this._activeDate; }
/**
 * @param {?} value
 * @return {?}
 */
set activeDate(value: D) {
    let /** @type {?} */ oldActiveDate = this._activeDate;
    this._activeDate = coerceDateProperty(this._dateAdapter, value) || this._dateAdapter.today();
    if (this._dateAdapter.getYear(oldActiveDate) != this._dateAdapter.getYear(this._activeDate)) {
      this._init();
    }
  }
private _activeDate: D;
/**
 * The currently selected date.
 * @return {?}
 */
get selected(): D | null { return this._selected; }
/**
 * @param {?} value
 * @return {?}
 */
set selected(value: D | null) {
    this._selected = coerceDateProperty(this._dateAdapter, value);
    this._selectedMonth = this._getMonthInCurrentYear(this._selected);
  }
private _selected: D | null;
/**
 * A function used to filter which dates are selectable.
 */
dateFilter: (date: D) => boolean;
/**
 * Emits when a new month is selected.
 */
selectedChange = new EventEmitter<D>();
/**
 * Grid of calendar cells representing the months of the year.
 */
_months: MdCalendarCell[][];
/**
 * The label for this year (e.g. "2017").
 */
_yearLabel: string;
/**
 * The month in this year that today falls on. Null if today is in a different year.
 */
_todayMonth: number | null;
/**
 * The month in this year that the selected Date falls on.
 * Null if the selected Date is in a different year.
 */
_selectedMonth: number | null;
/**
 * @param {?} _dateAdapter
 * @param {?} _dateFormats
 */
constructor(
public _dateAdapter: DateAdapter<D>,
private _dateFormats: MdDateFormats) {
    if (!this._dateAdapter) {
      throw createMissingDateImplError('DateAdapter');
    }
    if (!this._dateFormats) {
      throw createMissingDateImplError('MD_DATE_FORMATS');
    }

    this._activeDate = this._dateAdapter.today();
  }
/**
 * @return {?}
 */
ngAfterContentInit() {
    this._init();
  }
/**
 * Handles when a new month is selected.
 * @param {?} month
 * @return {?}
 */
_monthSelected(month: number) {
    let /** @type {?} */ daysInMonth = this._dateAdapter.getNumDaysInMonth(
        this._dateAdapter.createDate(this._dateAdapter.getYear(this.activeDate), month, 1));
    this.selectedChange.emit(this._dateAdapter.createDate(
        this._dateAdapter.getYear(this.activeDate), month,
        Math.min(this._dateAdapter.getDate(this.activeDate), daysInMonth)));
  }
/**
 * Initializes this month view.
 * @return {?}
 */
private _init() {
    this._selectedMonth = this._getMonthInCurrentYear(this.selected);
    this._todayMonth = this._getMonthInCurrentYear(this._dateAdapter.today());
    this._yearLabel = this._dateAdapter.getYearName(this.activeDate);

    let /** @type {?} */ monthNames = this._dateAdapter.getMonthNames('short');
    // First row of months only contains 5 elements so we can fit the year label on the same row.
    this._months = [[0, 1, 2, 3], [4, 5, 6, 7], [8, 9, 10, 11]].map(row => row.map(
        month => this._createCellForMonth(month, monthNames[month])));
  }
/**
 * Gets the month in this year that the given Date falls on.
 * Returns null if the given Date is in another year.
 * @param {?} date
 * @return {?}
 */
private _getMonthInCurrentYear(date: D | null) {
    return date && this._dateAdapter.getYear(date) == this._dateAdapter.getYear(this.activeDate) ?
        this._dateAdapter.getMonth(date) : null;
  }
/**
 * Creates an MdCalendarCell for the given month.
 * @param {?} month
 * @param {?} monthName
 * @return {?}
 */
private _createCellForMonth(month: number, monthName: string) {
    let /** @type {?} */ ariaLabel = this._dateAdapter.format(
        this._dateAdapter.createDate(this._dateAdapter.getYear(this.activeDate), month, 1),
        this._dateFormats.display.monthYearA11yLabel);
    return new MdCalendarCell(
        month, monthName.toLocaleUpperCase(), ariaLabel, this._isMonthEnabled(month));
  }
/**
 * Whether the given month is enabled.
 * @param {?} month
 * @return {?}
 */
private _isMonthEnabled(month: number) {
    if (!this.dateFilter) {
      return true;
    }

    let /** @type {?} */ firstOfMonth = this._dateAdapter.createDate(
        this._dateAdapter.getYear(this.activeDate), month, 1);

    // If any date in the month is enabled count the month as enabled.
    for (let /** @type {?} */ date = firstOfMonth; this._dateAdapter.getMonth(date) == month;
         date = this._dateAdapter.addCalendarDays(date, 1)) {
      if (this.dateFilter(date)) {
        return true;
      }
    }

    return false;
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: 'md-year-view, mat-year-view',
  templateUrl: 'year-view.html',
  encapsulation: ViewEncapsulation.None,
  preserveWhitespaces: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: DateAdapter, decorators: [{ type: Optional }, ]},
{type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [MD_DATE_FORMATS, ] }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'activeDate': [{ type: Input },],
'selected': [{ type: Input },],
'dateFilter': [{ type: Input },],
'selectedChange': [{ type: Output },],
};
}

function MdYearView_tsickle_Closure_declarations() {
/** @type {?} */
MdYearView.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdYearView.ctorParameters;
/** @type {?} */
MdYearView.propDecorators;
/** @type {?} */
MdYearView.prototype._activeDate;
/** @type {?} */
MdYearView.prototype._selected;
/**
 * A function used to filter which dates are selectable.
 * @type {?}
 */
MdYearView.prototype.dateFilter;
/**
 * Emits when a new month is selected.
 * @type {?}
 */
MdYearView.prototype.selectedChange;
/**
 * Grid of calendar cells representing the months of the year.
 * @type {?}
 */
MdYearView.prototype._months;
/**
 * The label for this year (e.g. "2017").
 * @type {?}
 */
MdYearView.prototype._yearLabel;
/**
 * The month in this year that today falls on. Null if today is in a different year.
 * @type {?}
 */
MdYearView.prototype._todayMonth;
/**
 * The month in this year that the selected Date falls on.
 * Null if the selected Date is in a different year.
 * @type {?}
 */
MdYearView.prototype._selectedMonth;
/** @type {?} */
MdYearView.prototype._dateAdapter;
/** @type {?} */
MdYearView.prototype._dateFormats;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
